import React, { useState, useEffect } from 'react';
import { FaTrash } from 'react-icons/fa';
import './styles.css';
import Swal from 'sweetalert2';

const WalletFundRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);

  // Fetch rules on component mount
  useEffect(() => {
    fetchRules();
  }, []);

  // Fetch all rules from API
  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api.experts4u.in/api/v2/Wallet/GetWalletRule', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Transform the data to split cardAmount into start and end values
        const transformedRules = result.data.map(rule => {
          const [startCardAmount, endCardAmount] = rule.cardAmount.split('-');
          return {
            ...rule,
            startCardAmount,
            endCardAmount
          };
        });
        setRules(transformedRules || []);
      } else {
        throw new Error(result.message || 'Failed to fetch rules');
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to fetch rules'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add a new empty rule
  const addNewRule = () => {
    setRules([...rules, { startCardAmount: '', endCardAmount: '', walletAmount: '', label: '', _id: null, fullAmount: false }]);
  };

  // Handle input changes
  const handleInputChange = (index, field, value) => {
    const updatedRules = [...rules];
    updatedRules[index] = {
      ...updatedRules[index],
      [field]: value
    };
    setRules(updatedRules);
  };

  // Handle full amount checkbox change
  const handleFullAmountChange = (index) => {
    const updatedRules = [...rules];
    if (!updatedRules[index].fullAmount) {
      updatedRules[index] = {
        ...updatedRules[index],
        endCardAmount: '',
        fullAmount: true
      };
    } else {
      updatedRules[index] = {
        ...updatedRules[index],
        fullAmount: false
      };
    }
    setRules(updatedRules);
  };

  // Delete a rule
  const handleDelete = async (id, index) => {
    try {
      // If it's a new rule (no id), just remove it from state
      if (!id) {
        const updatedRules = rules.filter((_, i) => i !== index);
        setRules(updatedRules);
        return;
      }

      // For existing rules, show confirmation and delete from API
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        setLoading(true);
        const response = await fetch(`https://api.experts4u.in/api/v2/Wallet/DeleteWalletRule/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        const data = await response.json();

        if (data.success) {
          // Remove the rule from state
          const updatedRules = rules.filter((_, i) => i !== index);
          setRules(updatedRules);
          
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Rule has been deleted successfully.'
          });
        } else {
          throw new Error(data.message || 'Failed to delete rule');
        }
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to delete rule'
      });
    } finally {
      setLoading(false);
    }
  };

  // Save all rules
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all rules
    const invalidRules = rules.filter(rule => !rule.startCardAmount  || !rule.label);
    if (invalidRules.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill all fields before submitting'
      });
      return;
    }

    try {
      setLoading(true);
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };

      // Process each rule
      for (const rule of rules) {
        if (!rule._id) {
          // Add new rule
          const response = await fetch('https://api.experts4u.in/api/v2/Wallet/AddWalletRule', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              cardAmount: `${rule.startCardAmount}-${rule.endCardAmount}`,
              walletAmount: rule.walletAmount,
              label: rule.label,
              fullAmount: rule.fullAmount
            })
          });

          const data = await response.json();
          if (!data.success) {
            throw new Error(data.message || 'Failed to save rule');
          }
        } else {
          // Update existing rule
          const response = await fetch(`https://api.experts4u.in/api/v2/Wallet/UpdateWalletRule/${rule._id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              cardAmount: `${rule.startCardAmount}-${rule.endCardAmount}`,
              walletAmount: rule.walletAmount,
              label: rule.label
            })
          });

          const data = await response.json();
          if (!data.success) {
            throw new Error(data.message || 'Failed to update rule');
          }
        }
      }

      setMessage('Rules saved successfully!');
      setMessageType('success');
      fetchRules(); // Refresh the rules list
    } catch (error) {
      console.error('Error saving rules:', error);
      setMessage(error.message || 'Failed to save rules');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Check if any rule has empty fields
  const isAddButtonDisabled = rules.some(rule => !rule.startCardAmount  || !rule.label);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-12">
          <div className="card p-4 shadow-sm">
            <div className="card-body">
              <h2 className="text-center mb-4">Wallet Fund Rules</h2>
              
              <div className="text-end mb-3">
                <button
                  className={`btn ${isAddButtonDisabled ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={addNewRule}
                  disabled={isAddButtonDisabled}
                >
                  Add New Rule +
                </button>
              </div>

          
              <div className="row mb-2">
                <div className="col-2">
                  <div className="text-muted small">Label Type</div>
                </div>
                <div className="col-3">
                  <div className="text-muted small">Start Cart Amount</div>
                </div>
                <div className="col-3">
                  <div className="text-muted small">End Cart Amount</div>
                </div>
                  <div className="col-2">
                    <div className="text-muted small">Wallet Amount</div>
                  </div>
                  <div className="col-1">
                    <div className="text-muted small">Full Amount</div>
                  </div>
                  <div className="col-1">
                    <div className="text-muted small">Action</div>
                  </div>
                  
                  
              </div>

              {rules.map((rule, index) => (
                <div className="row mb-3" key={index}>
                  <div className="col-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Label"
                      value={rule.label}
                      onChange={(e) => handleInputChange(index, 'label', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-3">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Start Amount"
                      value={rule.startCardAmount}
                      onChange={(e) => handleInputChange(index, 'startCardAmount', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-3">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="End Amount"
                      value={rule.endCardAmount}
                      onChange={(e) => handleInputChange(index, 'endCardAmount', e.target.value)}
                      required
                      disabled={rule.fullAmount}
                    />
                  </div>
                  <div className="col-2">
                    {
                      (rule.fullAmount || !rule.endCardAmount) ?
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Full Amount"
                        disabled={rule.fullAmount}
                        required
                      />
                      :
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Wallet Amount"
                        value={rule.walletAmount}
                        onChange={(e) => handleInputChange(index, 'walletAmount', e.target.value)}
                        disabled={rule.fullAmount}
                        required
                      />
                    }
                  </div>
                  <div className="col-1 d-flex justify-content-center align-items-center">
                    {(!String(rule.walletAmount) || !String(rule.endCardAmount)) && (
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        onChange={() => handleFullAmountChange(index)} 
                        checked={rule.fullAmount} 
                      />
                    )}
                  </div>
                  <div className="col-1">
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDelete(rule._id, index)}
                      disabled={loading}
                    >
                      <FaTrash />
                    </button>
                  </div>
                  
                </div>
              ))}

              <div className="text-center mt-4">
                <button 
                  className={`btn ${isAddButtonDisabled ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={handleSubmit}
                  disabled={isAddButtonDisabled || loading || rules.length === 0}
                >
                  {loading ? 'Saving...' : 'Save Rules'}
                </button>
              </div>

              {message && (
                <div className={`alert mt-3 ${messageType === 'success' ? 'alert-success' : 'alert-danger'}`}>
                  {message}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletFundRules;
